import {
  getStatusColor,
  getStatusString,
  TRAINER_INVITATION_STATUS
} from '@treneo/features/organizations';
import type { ITrainerInvitationDto } from '@treneo/models';
import { useFormatDateTime } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { CancelIcon, ExpandMoreIcon, IconSizes } from '@treneo/shared/icons';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LoadingIconButton,
  Row,
  Typography
} from '@treneo/shared/ui';

type DateLike = string | Date | null | undefined;

type Props = Readonly<{
  invitation: ITrainerInvitationDto;
  isExpanded: boolean;
  onToggleDetails: () => void;
  onCancel: () => void;
  isCancelPending: boolean;
}>;

export const CourseInvitationRow = ({
  invitation,
  isExpanded,
  onToggleDetails,
  onCancel,
  isCancelPending
}: Props) => {
  const { t } = useAppTranslations();
  const { formatDateTime } = useFormatDateTime();
  const status = Number(invitation.status);
  const isPending = status === TRAINER_INVITATION_STATUS.PENDING;
  const isAccepted = status === TRAINER_INVITATION_STATUS.ACCEPTED;
  const isExpired = status === TRAINER_INVITATION_STATUS.EXPIRED;
  const isCancelled = status === TRAINER_INVITATION_STATUS.CANCELLED;
  const isRejected = status === TRAINER_INVITATION_STATUS.REJECTED;

  const createdAtValue = invitation.createdAt as DateLike;
  const acceptedAtValue = invitation.acceptedAt as DateLike;
  const cancelledAtValue = invitation.cancelledAt as DateLike;
  const rejectedAtValue = invitation.rejectedAt as DateLike;
  const expiresAtValue = invitation.expiresAt as DateLike;
  const fallback = '—';
  const sentAtFormatted = formatDateTime(createdAtValue, fallback);
  const acceptedAtFormatted = formatDateTime(acceptedAtValue, fallback);
  const cancelledAtFormatted = formatDateTime(cancelledAtValue, fallback);
  const rejectedAtFormatted = formatDateTime(rejectedAtValue, fallback);
  const expiresAtFormatted = formatDateTime(expiresAtValue, fallback);

  const expandAriaLabel = isExpanded
    ? t('trainers.hideDetails', { defaultValue: 'Hide details' })
    : t('trainers.showDetails', { defaultValue: 'Show details' });

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: 'action.hover'
      }}
    >
      <Row spacing={1} alignItems='center' sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Typography variant='body2' component='span'>
          {invitation.email}
        </Typography>
        <Chip
          label={t(`trainers.invitationStatus.${getStatusString(status)}`)}
          color={getStatusColor(status)}
          size='small'
        />
        {isPending && (
          <LoadingIconButton
            icon={<CancelIcon fontSize={IconSizes.small} />}
            isLoading={isCancelPending}
            size='small'
            color='error'
            onClick={onCancel}
            aria-label={t('trainers.cancelInvitation')}
            title={t('trainers.cancelInvitation')}
          />
        )}
        <IconButton
          size='small'
          onClick={onToggleDetails}
          aria-label={expandAriaLabel}
          title={expandAriaLabel}
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s'
          }}
        >
          <ExpandMoreIcon fontSize={IconSizes.small} />
        </IconButton>
      </Row>
      <Collapse in={isExpanded}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            pl: 1,
            pt: 0.5,
            pb: 0.5
          }}
        >
          <Typography variant='caption' color='text.secondary' component='span'>
            {t('trainers.sentAt')}: {sentAtFormatted}
          </Typography>
          {isPending && (
            <Typography variant='caption' color='text.secondary' component='span'>
              {t('trainers.expiresAt')}: {expiresAtFormatted}
            </Typography>
          )}
          {isAccepted && invitation.acceptedAt != null && (
            <Typography variant='caption' color='text.secondary' component='span'>
              {t('trainers.acceptedAt')}: {acceptedAtFormatted}
            </Typography>
          )}
          {isExpired && (
            <Typography variant='caption' color='text.secondary' component='span'>
              {t('trainers.expiredAt')}: {expiresAtFormatted}
            </Typography>
          )}
          {isCancelled && (
            <Typography variant='caption' color='text.secondary' component='span'>
              {t('trainers.cancelledAt')}: {cancelledAtFormatted}
            </Typography>
          )}
          {isRejected && (
            <Typography variant='caption' color='text.secondary' component='span'>
              {t('trainers.rejectedAt')}: {rejectedAtFormatted}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
